import { useProfile } from '@/src/zustand/store'
import { ADMIN_MODAL_TYPE } from '@data/modalTypes'
import { useModalStore } from '@modal'
import { Avatar, Box, Card, DropButton, ResponsiveContext, Text } from 'grommet'
import { Down, FormClose, Menu } from 'grommet-icons'
import { Suspense, useContext, useEffect, useRef, useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import AuthApi from '../../../api/auth'
import { LockIcon, LogoutIcon, PlayIcon, SettingsIcon } from '../../../assets/svgs'
import { src } from '../../../constants'
import { NotificationBell } from '../../../notifications'
import { palette } from '../../../themes/palette'
import { Button } from '../Button'
import { InstantLoading } from '../Loader'
import { Sidebar } from '../Sidebar'
import { SmallText } from '../Text'

const AdminLayout = () => {
  const size = useContext(ResponsiveContext)
  const { logout } = AuthApi()
  const { user, role } = useProfile()
  const helpButtonRef = useRef<HTMLDivElement | null>(null)
  const { activeModal, closeModal, openModal } = useModalStore()
  const isHelpGuideOpen = activeModal.some(({ modal }) => modal === ADMIN_MODAL_TYPE.ADMIN_HELP_GUIDE)
  const { pathname } = useLocation()
  const isPatcientChart = pathname.includes('/patient-chart') || pathname.includes('/patient-mar/')
  const [tabletNavOpen, setTabletNavOpen] = useState(false)

  const handleHelpGuideModal = () => {
    if (isHelpGuideOpen) {
      closeModal(ADMIN_MODAL_TYPE.ADMIN_HELP_GUIDE)
      return
    }

    const buttonRect = helpButtonRef.current?.getBoundingClientRect()
    const guideTop = (buttonRect?.bottom || 90) + 12
    const guideLeft = Math.max((buttonRect?.left || 24) - 340, 16)

    openModal(ADMIN_MODAL_TYPE.ADMIN_HELP_GUIDE, {
      guide: {
        left: guideLeft,
        minWidth: '360px',
        top: guideTop
      }
    })
  }

  useEffect(() => {
    setTabletNavOpen(false)
  }, [pathname])

  useEffect(() => {
    if (!tabletNavOpen) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setTabletNavOpen(false)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [tabletNavOpen])

  return (
    <>
      <div
        className={`flex h-screen w-full min-h-0 overflow-hidden bg-[#F5F5F5] ${size === 'large' || size === 'xlarge' ? 'mx-auto max-w-full' : ''}`}
      >
        {/* Desktop / large tablet: persistent sidebar */}
        <div className="hidden shrink-0 overflow-y-auto py-5 pl-5 pr-0 lg:flex">
          <Sidebar />
        </div>

        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden p-3 md:p-4">
          <Box align="center" className="gap-3" direction="row" flex={false} justify="between" pad={{ bottom: '12px' }}>
            <div className="flex min-w-0 items-center gap-2 md:gap-3">
              <button
                aria-label="Open navigation menu"
                className="inline-flex shrink-0 items-center justify-center rounded-lg border border-[#D9D9D9] bg-white p-2 text-[#333] lg:hidden"
                onClick={() => setTabletNavOpen(true)}
                type="button"
              >
                <Menu color="#333" size="20px" />
              </button>
              <Box className="hidden md:block" width="268px">
                {/* Search reserved for desktop */}
              </Box>
            </div>
            <Box align="center" className="min-w-0" direction="row" gap="12px">
              <Box align="center" className="hidden sm:flex" direction="row" gap="12px">
                <Box>
                  <div
                    className="cursor-pointer flex place-items-center"
                    onClick={handleHelpGuideModal}
                    ref={helpButtonRef}
                    style={{ position: 'relative', zIndex: isHelpGuideOpen ? 2000 : 'auto' }}
                  >
                    <Button
                      className="!text-black !text-sm"
                      icon={<PlayIcon />}
                      kinds={'secondary'}
                      label="Video Guides"
                      reverse
                      type="button"
                    />
                  </div>
                </Box>
              </Box>
              <Box align="center" direction="row" gap="12px">
                <NotificationBell audience="staff" />
                <Box border={{ color: '#ddd', side: 'left', size: '1px' }} className="hidden sm:block" />
                <DropButton
                  alignSelf="center"
                  dropContent={
                    <Box pad={{ vertical: '28px' }} width="270px">
                      <Box align="center" gap="12px" pad={{ bottom: '18px' }}>
                        <Avatar size="60px" src={src} />
                        <Box>
                          <SmallText size="20px" style={{ textAlign: 'center', textTransform: 'capitalize' }}>
                            {user?.first_name} {user?.last_name}
                          </SmallText>
                          <Text size="12px" style={{ opacity: 0.4, textAlign: 'center' }}>
                            johndoe@gmail.com
                          </Text>
                        </Box>
                      </Box>
                      <Box border={{ color: palette.border, side: 'top' }} pad={{ horizontal: '14px', top: '18px' }}>
                        <Box className="staff-container" style={{ gap: '10px' }}>
                          <Box
                            align="center"
                            aria-disabled
                            direction="row"
                            gap="10px"
                            pad={{ bottom: '10px' }}
                            style={{ cursor: 'not-allowed' }}
                          >
                            <Box align="center" background="#BDBDBD" justify="center" pad="8px" round="100%">
                              <SettingsIcon color="white" height="16px" width="16px" />
                            </Box>
                            <Text color="#8A8A8A" size="14px">
                              Setting
                            </Text>
                          </Box>
                          <Box
                            align="center"
                            aria-disabled
                            direction="row"
                            gap="10px"
                            pad={{ bottom: '10px' }}
                            style={{ cursor: 'not-allowed' }}
                          >
                            <Box align="center" background="#BDBDBD" justify="center" pad="8px" round="100%">
                              <LockIcon color="white" height="16px" width="16px" />
                            </Box>
                            <Text color="#8A8A8A" size="14px">
                              Edit Password
                            </Text>
                          </Box>
                          <Box align="center" direction="row" gap="10px" onClick={logout} style={{ cursor: 'pointer' }}>
                            <Box align="center" background={palette.asparagus} justify="center" pad="8px" round="100%">
                              <LogoutIcon color="white" height="16px" width="16px" />
                            </Box>
                            <Text size="14px">Logout</Text>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  }
                  dropProps={{ align: { top: 'bottom' }, margin: { left: '-10px', top: '10px' }, round: '15px' }}
                >
                  <Box align="center" direction="row" gap="8px">
                    <Box direction="row" gap="8px">
                      <Avatar size="35px" src={src} />
                      <Box className="hidden md:block">
                        <Text size="14px" style={{ textTransform: 'capitalize' }}>
                          {user?.first_name} {user?.last_name}
                        </Text>
                        <Text size="11px" style={{ opacity: 0.4 }}>
                          {role[0]}
                        </Text>
                      </Box>
                    </Box>
                    <Down size="15px" />
                  </Box>
                </DropButton>
              </Box>
            </Box>
          </Box>
          <Card
            className={`flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-xl bg-white ${isPatcientChart ? 'p-0' : 'p-3 md:p-4'}`}
          >
            <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
              <Suspense fallback={<InstantLoading />}>
                <Outlet />
              </Suspense>
            </div>
          </Card>
        </div>
      </div>

      {/* Small / large tablet: overlay navigation drawer */}
      {tabletNavOpen ? (
        <div className="fixed inset-0 z-50 lg:hidden">
          <button
            aria-label="Close navigation overlay"
            className="absolute inset-0 bg-black/35"
            onClick={() => setTabletNavOpen(false)}
            type="button"
          />
          <div className="relative flex-1 flex h-full w-[min(86vw,230px)] flex-col bg-[#F5F5F5] p-3 shadow-xl">
            <button
              aria-label="Close navigation menu"
              className="absolute right-3 top-3 z-10 inline-flex rounded-md border border-[#D9D9D9] bg-white p-1.5 text-[#333]"
              onClick={() => setTabletNavOpen(false)}
              type="button"
            >
              <FormClose color="#333" size="18px" />
            </button>
            <div className="min-h-0 flex flex-col flex-1 overflow-y-auto pt-10">
              <Sidebar />
            </div>
          </div>
        </div>
      ) : null}
    </>
  )
}

export default AdminLayout
